import {
    BudgetBys,
    BudgetPhases,
    MANTY_ORDERED_BUDGET_PHASES,
} from 'business/constants';
import { getPhasedFieldsUntilPhase } from 'routes/BudgetModule/shared/phases/helpers';
import {
    BudgetProposalT,
    DraftOperationAssociationT,
    ResourceBudgetProposalAssociationT,
} from 'routes/BudgetModule/config/types';
import { snakify } from 'helpers/syntaxHelper';
import { RQAPICallWithData } from 'store/APIClient';
import { useQuery } from '@tanstack/react-query';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { ExpenseIncomeImputationDataT } from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/hooks/expenseIncomeImputations';
import { IS_REJECTED } from 'routes/BudgetModule/config/constants';
import { isRejectedForPhase } from 'routes/BudgetModule/shared/phases/phase';

export const ORDERED_LIST_METRIC = getPhasedFieldsUntilPhase(
    MANTY_ORDERED_BUDGET_PHASES[MANTY_ORDERED_BUDGET_PHASES.length - 1],
    'amount',
);

/**
 * Type guard: Checks if an item is of type InscriptionT based on the presence of the 'isRejected' property.
 */
const isInscriptionT = (
    item: InscriptionT | ExpenseIncomeImputationDataT,
): item is InscriptionT => {
    return IS_REJECTED in item;
};

/**
 * Filters out rejected inscriptions from a list of inscriptions or imputations.
 * In the context where it's used (cadrage), we assume that imputations are never rejected.
 */
export const filterRejectedInscriptionOrImputation = (
    inscriptionsOrImputation: (InscriptionT | ExpenseIncomeImputationDataT)[],
    phase?: BudgetPhases,
): (InscriptionT | ExpenseIncomeImputationDataT)[] => {
    if (inscriptionsOrImputation.length === 0) {
        return [];
    }

    return inscriptionsOrImputation.filter((item) => {
        if (isInscriptionT(item)) {
            return !isRejectedForPhase(item, phase);
        }
        return true;
    });
};

export const useGetResourcesByBudgetProposal = ({
    budgetBy,
}: {
    budgetBy: BudgetBys;
}) =>
    useQuery({
        queryKey: ['ResourcesByBudgetProposal', budgetBy],

        queryFn: async () => {
            const response = await RQAPICallWithData<
                ResourceBudgetProposalAssociationT[]
            >({
                endpoint: `budget_proposals/edition/resource_association/${snakify(budgetBy)}`,
                method: 'GET',
            });
            return response.content.data;
        },
    });

export const mergeAssociationData = ({
    budgetProposals,
    associations,
}: {
    budgetProposals: BudgetProposalT[] | null | undefined;
    associations: DraftOperationAssociationT[];
}): ResourceBudgetProposalAssociationT[] => {
    if (!budgetProposals || !associations) {
        return [];
    }

    const mergedAssociations: ResourceBudgetProposalAssociationT[] = [];

    associations.forEach((association) => {
        const budgetProposal = budgetProposals.find(
            (bp) => bp.id === association.budgetProposalId,
        );
        if (!budgetProposal) return;
        const resource = association.orgChartFinanceName
            ? {
                  resourceSoftwareId:
                      association.orgChartFinanceSoftwareId ?? '',
                  resourceName: association.orgChartFinanceName ?? '',
              }
            : {
                  resourceSoftwareId: association.gestionnaireSoftwareId ?? '',
                  resourceName: association.gestionnaireName ?? '',
              };

        mergedAssociations.push({ budgetProposal, resource });
    });

    return mergedAssociations;
};
